import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { KeyboardDatePickerController } from '../../../components/organisms/form/controllers/KeyboardDatePickerController';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { useCouponPackUsageStatusDetail } from '../hooks/useCouponPackUsageStatusDetail';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { formatJstDate, validateStartEnd } from '../../../util';
import { useUrlSearchParam } from '../../../components/contexts/hooks/url';
import { TextFieldController } from '../../../components/organisms/form/controllers/TextFieldController';
import Combobox from '../../../components/atom/Combobox';

const useStyles = makeStyles((theme: Theme) => ({
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  resultList: {
    marginTop: theme.spacing(3),
  },
  groupButton: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonSearch: {
    width: '232px',
    marginTop: '0px',
  },
  buttonExport: {
    width: '232px',
    marginLeft: '44px',
    marginTop: '0px',
  },
}));

const COUPON_STATUS_OPTIONS = [
  { label: '発行済み', value: 'issued' },
  { label: '使用済み', value: 'used' },
];

export type CouponPackUsageStatusDetailType = {
  id: string;
  issuedBy: string;
  couponCode: string;
  couponName: string;
  CouponPackName: string;
  CouponPackId: string;
  CouponPackUrl: string;
  status: string;
  datetime: string;
  usedBy: string;
};

export type SearchFormValues = {
  startDate: Date | null;
  endDate: Date | null;
  usageShopName: string;
  couponStatus: string;
};

export const CouponPackUsageStatusDetailPage: React.FC = () => {
  const { couponPackMasterId } = useParams<{ couponPackMasterId: string }>();
  const searchParams = useUrlSearchParam();
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const props = useCouponPackUsageStatusDetail({
    couponPackMasterId,
    couponPackCode: searchParams.get('couponPackCode') || '',
    couponPackName: searchParams.get('couponPackName') || '',
    couponCode: searchParams.get('couponCode') || '',
    couponName: searchParams.get('couponName') || '',
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      startDate: searchParams.get('startDate') ? new Date(searchParams.get('startDate')!) : null,
      endDate: searchParams.get('endDate') ? new Date(searchParams.get('endDate')!) : null,
      usageShopName: '',
      couponStatus: '',
    },
  });

  const { handleSubmit, watch, control } = searchForm;

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'issuedBy',
        label: '発行した組織',
        minWidth: theme.spacing(16),
        align: 'left',
      },
      {
        name: 'CouponPackName',
        label: '回数券名',
        minWidth: theme.spacing(14),
        align: 'left',
      },
      {
        name: 'CouponPackId',
        label: '回数券ID',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'CouponPackUrl',
        label: '回数券URL',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'couponCode',
        label: 'クーポンコード',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'couponName',
        label: 'クーポン名',
        minWidth: theme.spacing(15),
        align: 'left',
      },
      {
        name: 'status',
        label: 'ステータス',
        minWidth: theme.spacing(14),
        align: 'left',
      },
      {
        name: 'usageDatetime',
        label: '利用日時',
        minWidth: theme.spacing(16),
        align: 'left',
      },
      {
        name: 'usageShopName',
        label: '利用店舗',
        minWidth: theme.spacing(13),
        align: 'left',
      },
      {
        name: 'action',
        label: '',
        minWidth: theme.spacing(5),
        align: 'center',
      },
    ],
  };

  const validateRangeDatePicker = (data: SearchFormValues): boolean => {
    const startDate = new Date(data.startDate!);
    const endDate = new Date(data.endDate!);

    const startDateTime = DateTime.fromJSDate(startDate);
    const endDateTime = DateTime.fromJSDate(endDate);

    if (!startDateTime.isValid || !endDateTime.isValid) {
      const key = enqueueSnackbar('表示期間、有効期限、取得期限の日時情報を正しく入力してください', {
        variant: 'warning',
        autoHideDuration: 5000,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    if (data.startDate && data.endDate && !validateStartEnd(startDateTime, endDateTime, true)) {
      const key = enqueueSnackbar('開始日時が前になるように修正してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    return true;
  };

  const onSearch = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onSearch(data);
  };

  const onCsvOut = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onCsvOut(data);
  };

  useEffect(() => {
    // init search
    props.onSearch(searchForm.getValues());
  }, []);

  return (
    <MenuHeaderTemplate
      header={{
        title: '回数券利用状況一覧',
        loading: undefined,
        progressLabel: undefined,
      }}
    >
      <Container maxWidth="xl">
        <Box mb={3}>
          <Breadcrumbs>
            <Link
              href="#"
              onClick={() => {
                const searchParams = new URLSearchParams(history.location.search);
                const organization = searchParams.get('organization');

                history.push({
                  pathname: '/coupon-pack-usage-status',
                  search: `?organization=${organization || ''}`,
                });
              }}
            >
              回数券利用状況集計
            </Link>
            <Typography variant="body1" color="textPrimary">
              {searchParams.get('couponName')}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormProvider {...searchForm}>
              <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="startDate"
                        label="利用日時"
                        maxDate={
                          watch('endDate') &&
                          new Date(watch('endDate')!) instanceof Date &&
                          !isNaN(new Date(watch('endDate')!).getTime())
                            ? formatJstDate(new Date(watch('endDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item>
                      <Box sx={{ fontSize: '2rem', height: '24px' }}>-</Box>
                    </Grid>
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="endDate"
                        label="利用日時"
                        minDate={
                          watch('startDate') &&
                          new Date(watch('startDate')!) instanceof Date &&
                          !isNaN(new Date(watch('startDate')!).getTime())
                            ? formatJstDate(new Date(watch('startDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container item xs={12} spacing={2}>
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <TextFieldController
                          name="usageShopName"
                          label="利用店舗"
                          size="small"
                          className={classes.buttonSearch}
                          rule={{
                            maxLength: {
                              value: 30,
                              message: '30文字以内で入力してください',
                            },
                          }}
                        />
                        <Box className={classes.buttonExport} mb={1}>
                          <Combobox
                            control={control}
                            placeholder="クーポンステータス"
                            name="couponStatus"
                            size="medium"
                            inputReadOnly
                            options={COUPON_STATUS_OPTIONS}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <Button
                          type="submit"
                          variant={'outlined'}
                          size={'medium'}
                          color={'default'}
                          fullWidth
                          endIcon={<Search />}
                          disabled={props.loading || props.csvLoading}
                          className={classes.buttonSearch}
                        >
                          検索
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          color={'secondary'}
                          fullWidth
                          className={classes.buttonExport}
                          onClick={handleSubmit(onCsvOut)}
                          disabled={props.loading || props.csvLoading}
                        >
                          {props.csvLoading ? <CircularProgress size={26} /> : 'CSV出力'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Grid>

          <Grid item xs={12} className={classes.resultList}>
            <TableContainer component={Paper}>
              {props.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!props.allAcquired && !props.data?.nodes) {
                      return;
                    } else if (!props.data?.nodes || props.data?.nodes.length === 0) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return props.data?.nodes.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {record.organizationName}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponPackName}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponPackId}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponPackUrl ? (
                              <Link href={record.couponPackUrl} target="_blank" rel="noopener noreferrer">
                                {record.couponPackUrl}
                              </Link>
                            ) : null}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponCode}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponName}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {record.logType}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {record.timestamp}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.shopName}
                          </TableCell>
                          <TableCell align="center" scope="row">
                            <Button
                              onClick={() =>
                                history.push({
                                  pathname: `/coupon-masters/${record.couponMasterId}/coupons/${record.couponId}`,
                                  search: history.location.search,
                                })
                              }
                              type="button"
                              size="small"
                              color="primary"
                            >
                              明細
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {props.data?.nodes?.length && !props.allAcquired ? (
            <ReadMoreButton
              fetchMore={() => {
                props.onFetchMore(searchForm.getValues());
              }}
              loading={props.loading}
            />
          ) : (
            <></>
          )}
        </Grid>
      </Container>
    </MenuHeaderTemplate>
  );
};
